import type { Account, UserContactData } from "@onestore/api/account"
import type { PriceValue } from "@onestore/api/basket"
import type { OnestoreVarsUrl } from "~/lib/url" // TODO: do wyprostowania zależności między paczkami
import type {
  MicrosoftSpecialQualifications,
  MicrosoftTenantType,
} from "~/store/office/constants"
import type {
  AccountMessage,
  AccountType,
  CurrencyCode,
  CurrencySign,
  PeriodName,
  ProductAlias,
  TaxNumberData,
  TaxRate,
  TaxValue,
} from "../api/types"

export type ProductContentName = string

export type BasketResource = {
  last_patch_operation_flag: boolean
}

export type BasketItem = {
  plan: number
  planPeriod: number
  parameters?: Record<string, string | boolean | number>
  quantity: number
  price: PriceValue
  resources: BasketResource[]
  children: BasketItem[]
  last_patch_operation_flag: boolean
  id: number
  sku: string
  name: string
  ignoreHack?: boolean
}

export type BasketUpdateItem = {
  id: number
  plan?: number
  planPeriod?: number
  quantity?: number
}

export type OnestoreVarsCommon = {
  affiliateCookieName: string
  apiUrl: string
  basketUrl: string
  bonusUrl: string
  brand: string
  build: string
  companyNumberCheck: boolean
  currency: CurrencySign
  currencyCode: CurrencyCode
  dimensions: string[]
  domainsBonusUrl: string
  domainSearchUrl: string
  failedUrl: string
  finalizeUrl: string
  helpUrl: string
  hostingHackPlanId: number | null
  secondaryHackPlanId: number | null
  alternativeHostingHackPlanId: number | null
  locale: string
  mainPoolId: number
  passwordRecoverUrl: string
  pricePrecision: number
  productsIndex: Record<number, ProductAlias>
  reCaptchaSiteKey: string
  rootCookieDomain: string
  smsCaptchaEnabled: boolean
  smsConfirmationFormat: string
  smsCount: number
  smsErrors: number
  successfulUrl: string
  taxValue: TaxValue
  taxRate: TaxRate
  version: number
  wikiUrl: string
  helpContactUrl: string
  isSessionEnabled: boolean
  isTacosEnabled: boolean
  ceneoOpinionsQuestionnaireDays?: number
  ceneoOpinionsAccountGuid?: string
}

export type OnestoreVarsPricing = {
  currency: CurrencySign
  pricePrecision: number
  taxValue: TaxValue
  taxRate: TaxRate
}

export type OnestoreVarsI18n = {
  locale: string
}

export type OnestoreVars = OnestoreVarsCommon &
  OnestoreVarsI18n &
  OnestoreVarsUrl &
  OnestoreVarsPricing

export type BonusUrlDefinition = {
  content?: ProductContentName
  product: ProductAlias
  upsell?: ProductAlias[]
  bundle?: ProductAlias
  upgrade?: ProductAlias
  period?: PeriodName
  code?: string
  value?: string
  domainSearch?: string
}

export declare namespace HTTP {
  export type Status = 303 | number
  export type Method = "PUT" | "GET" | "POST" | "PATCH" | "DELETE" | "OPTIONS"
}

export type FetchOptions = {
  headers?: HeadersInit
  method?: HTTP.Method
  body?: string
}

export type AliasParameters = Record<string, string>

export type AliasProductData = {
  alias: string
  template?: string
  parameters?: AliasParameters
}

export type DomainData = {
  extension?: string
  pool?: string
}

export type BasketProductsPE = {
  id?: number
  plan?: number
  name: string
  quantity: number
  sku: string
  price: number | undefined
  regular_price?: number
  discount?: number
  period: string
  domain_name?: string
  domain_extension?: string
  tax?: number
  children: BasketProductsPE[]
}

export type OfficeState = {
  domain_request: HTTP.Status
  domain_name: string
  errors: string[]
  account_request: HTTP.Status
  can_continue?: boolean
  has_error: boolean
  tenant: MicrosoftTenantType
  special_qualifications: MicrosoftSpecialQualifications
}

export enum ItemRemoval {
  ALLOW = "allow",
  DENY = "deny",
  CONFIRM_BUNDLE_ITEM = "item",
  CONFIRM_HACK_DOMAIN = "hack_domain",
  CONFIRM_HACK_ITEM = "hack_item",
}

export type NotificationMessage = {
  id: string
  type: "error" | "success" | "info" | "warning" | string
  title: string
  message: string
}

export type MessageState = {
  readonly global: (NotificationMessage | AccountMessage)[]
}

export type AccountState = {
  readonly account: Account | null
}

export type UserState = UserContactData & {
  tax?: TaxNumberData[]
  company_name?: string
  type: AccountType
  postal_address?: string
  email: string
  user_id?: string | null
}

export type PaymentRedirectData = {
  method: string
  data: Record<string, string>
  url: string
}
